import React, { useState, useEffect } from 'react';
import { coachService } from '../services/api';
import { Badge, Button, Card, Input } from './ui';

const CoachInvitationManager = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [message, setMessage] = useState(null);
  const [form, setForm] = useState({
    email: '',
    type: 'client',
    tier: 'free',
    trialDays: 30,
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      setLoading(true);
      const response = await coachService.getInvitations({ limit: 100 });
      setInvitations(response.data?.data?.invitations || []);
    } catch (error) {
      console.error('Failed to load invitations:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load invitations'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.email) {
      setMessage({ type: 'error', text: 'Email is required' });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setMessage({ type: 'error', text: 'Invalid email address' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        type: form.type,
        tier: form.tier,
        trialDays: parseInt(form.trialDays),
        message: form.message || `You're invited to join MHDF Momentum as a ${form.type}!`
      };

      await coachService.sendInvitation(payload);
      
      setMessage({
        type: 'success',
        text: `Invitation sent to ${form.email}`
      });

      // Reset form
      setForm({
        email: '',
        type: 'client',
        tier: 'free',
        trialDays: 30,
        message: ''
      });

      // Reload invitations
      await loadInvitations();
      
      // Close form after success
      setTimeout(() => setShowInviteForm(false), 1500);
    } catch (error) {
      console.error('Failed to send invitation:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to send invitation'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Cancel this invitation?')) {
      return;
    }

    try {
      await coachService.cancelInvitation(invitationId);
      setMessage({
        type: 'success',
        text: 'Invitation cancelled'
      });
      await loadInvitations();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      setMessage({
        type: 'error',
        text: 'Failed to cancel invitation'
      });
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      clinician: 'bg-blue-100 text-blue-800',
      client: 'bg-green-100 text-green-800',
      coach: 'bg-purple-100 text-purple-800',
      clergy: 'bg-amber-100 text-amber-800'
    };
    return <Badge className={colors[type] || 'bg-gray-100 text-gray-800'} size="sm">{type}</Badge>;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const pendingInvitations = invitations.filter(inv => inv.status === 'pending');
  const acceptedInvitations = invitations.filter(inv => inv.status === 'accepted');

  if (loading && invitations.length === 0) {
    return <div className="text-center py-8">Loading invitations...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Message Alert */}
      {message && (
        <div
          className={`rounded-lg border p-4 ${
            message.type === 'success'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-error-200 bg-error-50 text-error-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Send Invitation Form */}
      {showInviteForm ? (
        <Card>
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-neutral-900">Send New Invitation</h3>
            <p className="mt-1 text-sm text-neutral-600">Invite clients, clinicians, or other coaches to join the platform</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Email Address"
                type="email"
                value={form.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                placeholder="user@example.com"
                required
                fullWidth
              />

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  User Type
                </label>
                <select
                  value={form.type}
                  onChange={(e) => handleFormChange('type', e.target.value)}
                  className="w-full rounded-lg border-2 border-neutral-300 px-3 py-2 font-medium text-neutral-900 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="client">Client</option>
                  <option value="clinician">Clinician</option>
                  <option value="coach">Coach</option>
                  <option value="clergy">Clergy</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Tier
                </label>
                <select
                  value={form.tier}
                  onChange={(e) => handleFormChange('tier', e.target.value)}
                  className="w-full rounded-lg border-2 border-neutral-300 px-3 py-2 font-medium text-neutral-900 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="free">Free</option>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <Input
                label="Trial Days"
                type="number"
                value={form.trialDays}
                onChange={(e) => handleFormChange('trialDays', e.target.value)}
                min="0"
                max="365"
                fullWidth
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Custom Message (Optional)
              </label>
              <textarea
                value={form.message}
                onChange={(e) => handleFormChange('message', e.target.value)}
                placeholder="Add a custom message to the invitation..."
                className="w-full rounded-lg border-2 border-neutral-300 px-3 py-2 text-neutral-900 placeholder-neutral-400 focus:border-indigo-500 focus:outline-none"
                rows="3"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowInviteForm(false)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Send Invitation'}
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <Button onClick={() => setShowInviteForm(true)}>
          + Send New Invitation
        </Button>
      )}

      {/* Pending Invitations */}
      {pendingInvitations.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Pending Invitations</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Tier</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Sent</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Expires</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {pendingInvitations.map((inv) => (
                  <tr key={inv._id} className="hover:bg-neutral-50">
                    <td className="py-3 px-4">{inv.email}</td>
                    <td className="py-3 px-4">{getTypeBadge(inv.type)}</td>
                    <td className="py-3 px-4 text-neutral-600">{inv.tier}</td>
                    <td className="py-3 px-4 text-xs text-neutral-600">{formatDate(inv.createdAt)}</td>
                    <td className="py-3 px-4 text-xs text-neutral-600">{formatDate(inv.expiresAt)}</td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleCancelInvitation(inv._id)}
                        className="text-error-600 hover:text-error-700 font-semibold text-xs"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Accepted Invitations */}
      {acceptedInvitations.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Accepted Invitations</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-neutral-200">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Email</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Tier</th>
                  <th className="text-left py-3 px-4 font-semibold text-neutral-700">Accepted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {acceptedInvitations.map((inv) => (
                  <tr key={inv._id} className="hover:bg-neutral-50">
                    <td className="py-3 px-4">{inv.email}</td>
                    <td className="py-3 px-4">{getTypeBadge(inv.type)}</td>
                    <td className="py-3 px-4 text-neutral-600">{inv.tier}</td>
                    <td className="py-3 px-4 text-xs text-neutral-600">{formatDate(inv.acceptedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {invitations.length === 0 && !showInviteForm && (
        <Card className="text-center py-12">
          <p className="text-neutral-600">No invitations yet. Send one to get started!</p>
        </Card>
      )}
    </div>
  );
};

export default CoachInvitationManager;
